import {
    Check as CheckIcon,
    LiveHelp as LiveHelpIcon,
    PriorityHigh as PriorityHighIcon,
} from '@mui/icons-material';
import { Grid, Link, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import classNames from 'classnames';
import { CollapsiblePaper, DataColumns } from 'cleveron-web-components';
import { toDurationStringFromSeconds } from 'cleveron-web-components/utils';
import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import Item from './Item';

const useStyles = makeStyles(theme => ({
    data: {
        padding: theme.spacing(2),
    },
    dataTitle: {
        color: theme.palette.text.secondary,
    },
    finishedModule: {
        backgroundColor: theme.isDark() ? '#162c19' : '#DEF3DF',
    },
    icon: { verticalAlign: 'middle' },
    link: {
        paddingRight: theme.spacing(4),
    },
    module: {
        borderRadius: 0,
        boxShadow: 'none',
        margin: theme.spacing(0, 0, 1),
        paddingBottom: theme.spacing(2),
    },
    padding: {
        padding: theme.spacing(0),
    },
    unfinishedModule: {
        backgroundColor: theme.isDark()
            ? theme.palette.background.default
            : theme.palette.grey['100'],
    },
    warningIcon: {
        color: theme.palette.warning.main,
        paddingBottom: 0,
    },
}));

function Module({ disabled, id, module, issueId }) {
    const classes = useStyles();
    const [completedItems, setCompletedItems] = useState([]);
    const [hasProblemItems, setHasProblemItems] = useState([]);

    const info = [
        {
            key: 1,
            rows: [
                { key: 'eta', value: module.eta ? toDurationStringFromSeconds(module.eta) : '—' },
                { key: 'tools', value: module.tools.join('; ') },
            ],
        },
        {
            key: 2,
            rows: [{ key: 'description', value: module.description || '—' }],
        },
    ];

    const isFinished = completedItems.length === module.items.length;

    const wideChildren = useMemo(
        () =>
            module.items?.map((item, index) => (
                <Item
                    completedItems={completedItems}
                    disabled={disabled}
                    hasProblemItems={hasProblemItems}
                    id={`${id}Item`}
                    issueId={issueId}
                    item={item}
                    /* eslint-disable-next-line react/no-array-index-key */
                    key={index}
                    setCompletedItems={setCompletedItems}
                    setHasProblemItems={setHasProblemItems}
                />
            )),
        [completedItems, disabled, hasProblemItems, id, issueId, module.items]
    );

    return (
        <CollapsiblePaper
            className={classNames(classes.module, {
                [classes.finishedModule]: isFinished,
                [classes.unfinishedModule]: !isFinished,
            })}
            hasMoveDeleteButtons={false}
            headerComponent={
                <Grid alignItems="baseline" container item spacing={2}>
                    <Grid item xs="auto">
                        <Typography variant="h6">{module.name}</Typography>
                    </Grid>
                    {hasProblemItems.length > 0 && (
                        <Grid item xs="auto">
                            <PriorityHighIcon className={classes.warningIcon} />
                        </Grid>
                    )}
                    {isFinished ? (
                        <Grid item xs="auto">
                            <CheckIcon color="secondary" sx={{ mb: '-6px' }} />
                        </Grid>
                    ) : (
                        <Grid item xs="auto">
                            <Typography align="right" color="textSecondary" variant="body2">
                                {completedItems.length}/{module.items.length}
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            }
            id={`${id}Module`}
            wideChildren={wideChildren}
        >
            <Grid container spacing={2} sx={{ pb: 1 }}>
                <Grid item md={12}>
                    <DataColumns
                        alignItems="flex-start"
                        columns={info}
                        id={`${id}Info`}
                        titleClassName={classes.dataTitle}
                        translationKey="task.maintenance"
                    />
                </Grid>
                <Grid item xs={12}>
                    {module.manualLinks?.map((manualLink, index) => (
                        <Link
                            className={classes.link}
                            href={manualLink}
                            key={manualLink}
                            rel="noopener noreferrer"
                            target="_blank"
                            underline="hover"
                        >
                            <LiveHelpIcon className={classes.icon} />
                            <FormattedMessage
                                defaultMessage="MANUAL LINK {index}"
                                id="task.maintenance.manualLink"
                                values={{ index: index + 1 }}
                            />
                        </Link>
                    ))}
                </Grid>
            </Grid>
        </CollapsiblePaper>
    );
}
export default Module;
